import React from 'react'
import {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
} from '../../components/table'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UITableProps extends React.ComponentProps<typeof Table> {
  /**
   * 테이블의 크기
   */
  size?: 'sm' | 'default' | 'lg'
  /**
   * 테이블의 스타일 변형 (rounded가 기본값)
   */
  variant?: 'default' | 'bordered' | 'rounded' | 'striped'
  /**
   * 고정 레이아웃 사용 여부
   */
  fixedLayout?: boolean
  /**
   * 테이블에 대한 접근 가능한 설명
   */
  'aria-label'?: string
  /**
   * 테이블 설명을 참조하는 요소의 ID
   */
  'aria-labelledby'?: string
  /**
   * 테이블에 대한 추가 설명을 참조하는 요소의 ID
   */
  'aria-describedby'?: string
}

export interface UITableHeaderProps extends React.ComponentProps<typeof TableHeader> {}
export interface UITableBodyProps extends React.ComponentProps<typeof TableBody> {}
export interface UITableFooterProps extends React.ComponentProps<typeof TableFooter> {}

export interface UITableRowProps extends React.ComponentProps<typeof TableRow> {
  /**
   * 행의 상태
   */
  state?: 'default' | 'selected' | 'hover'
}

export interface UITableHeadProps extends React.ComponentProps<typeof TableHead> {
  /**
   * 정렬 가능 여부
   */
  sortable?: boolean
  /**
   * 정렬 방향
   */
  sortDirection?: 'asc' | 'desc' | null
  /**
   * 정렬 클릭 핸들러
   */
  onSort?: () => void
}

export interface UITableCellProps extends React.ComponentProps<typeof TableCell> {
  /**
   * 셀의 정렬
   */
  align?: 'left' | 'center' | 'right'
  /**
   * 셀이 숫자 데이터인지 여부
   */
  numeric?: boolean
}

export interface UITableCaptionProps extends React.ComponentProps<typeof TableCaption> {}

const sizeVariants = {
  sm: {
    table: "text-xs",
    head: "h-8 px-1.5",
    cell: "p-1.5"
  },
  default: {
    table: "text-sm",
    head: "h-10 px-2",
    cell: "p-2"
  },
  lg: {
    table: "text-base",
    head: "h-12 px-3",
    cell: "p-3"
  }
}

/**
 * 기본 테이블 컴포넌트 (Rounded Corners 기본)
 * 모던 스타일과 기본 기능을 제공하는 테이블 컴포넌트입니다.
 */
export const UITable = React.forwardRef<
  React.ComponentRef<typeof Table>,
  UITableProps
>(({
  className,
  size = "default",
  variant = "rounded",
  fixedLayout = false,
  children,
  ...props
}, ref) => {
  // 깔끔한 스타일링 적용
  const containerClasses = cn(
    "relative w-full overflow-hidden",
    variant === 'default' && "border border-border/50",
    variant === 'bordered' && "border border-border",
    variant === 'rounded' && "border border-border rounded-lg shadow-sm bg-background", // 접근성 개선: 완전 불투명
    variant === 'striped' && "border border-border rounded-lg shadow-sm bg-background"
  )

  return (
    <UIBox className={containerClasses}>
      <Table
        ref={ref}
        role="table"
        className={cn(
          sizeVariants[size].table,
          fixedLayout && "table-fixed",
          variant === 'striped' && "[&_tbody_tr:nth-child(odd)]:bg-muted/50",
          className
        )}
        {...props}
      >
        {children}
      </Table>
    </UIBox>
  )
})

export const UITableHeader = React.forwardRef<
  React.ComponentRef<typeof TableHeader>,
  UITableHeaderProps
>(({ className, ...props }, ref) => (
  <TableHeader
    ref={ref}
    role="rowgroup"
    className={cn("bg-muted/50 border-b border-border", className)}
    {...props}
  />
))

export const UITableBody = React.forwardRef<
  React.ComponentRef<typeof TableBody>,
  UITableBodyProps
>(({ className, ...props }, ref) => (
  <TableBody ref={ref} role="rowgroup" className={className} {...props} />
))

export const UITableFooter = React.forwardRef<
  React.ComponentRef<typeof TableFooter>,
  UITableFooterProps
>(({ className, ...props }, ref) => (
  <TableFooter ref={ref} className={className} {...props} />
))

export const UITableRow = React.forwardRef<
  React.ComponentRef<typeof TableRow>,
  UITableRowProps
>(({ className, state = "default", ...props }, ref) => (
  <TableRow
    ref={ref}
    role="row"
    className={cn(
      "border-b border-border/50 hover:bg-muted/30 transition-colors duration-200",
      state === 'selected' && "bg-muted/50",
      state === 'hover' && "bg-muted/30",
      className
    )}
    data-state={state !== "default" ? state : undefined}
    aria-selected={state === 'selected' ? 'true' : undefined}
    {...props}
  />
))

export const UITableHead = React.forwardRef<
  React.ComponentRef<typeof TableHead>,
  UITableHeadProps
>(({
  className,
  sortable = false,
  sortDirection = null,
  onSort,
  children,
  ...props
}, ref) => (
  <TableHead
    ref={ref}
    scope="col"
    className={cn(
      "h-10 px-3 text-left font-medium text-foreground/70",
      sortable && "cursor-pointer select-none hover:bg-muted/40 transition-colors focus:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-primary/20",
      className
    )}
    onClick={sortable ? onSort : undefined}
    onKeyDown={sortable ? (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onSort?.()
      }
    } : undefined}
    tabIndex={sortable ? 0 : undefined}
    role={sortable ? "columnheader button" : "columnheader"}
    aria-sort={
      sortable
        ? sortDirection === 'asc'
          ? 'ascending'
          : sortDirection === 'desc'
            ? 'descending'
            : 'none'
        : undefined
    }
    {...props}
  >
    <UIBox className="flex items-center gap-2">
      {children}
      {sortable && (
        <span
          className="ml-auto text-muted-foreground"
          aria-hidden="true"
        >
          {sortDirection === 'asc' && '↑'}
          {sortDirection === 'desc' && '↓'}
          {sortDirection === null && '↕'}
        </span>
      )}
    </UIBox>
  </TableHead>
))

export const UITableCell = React.forwardRef<
  React.ComponentRef<typeof TableCell>,
  UITableCellProps
>(({
  className,
  align = "left",
  numeric = false,
  ...props
}, ref) => (
  <TableCell
    ref={ref}
    role="cell"
    className={cn(
      "px-3 py-2 text-foreground",
      align === 'center' && "text-center",
      align === 'right' && "text-right",
      numeric && "text-right font-mono tabular-nums",
      className
    )}
    {...props}
  />
))

export const UITableCaption = React.forwardRef<
  React.ComponentRef<typeof TableCaption>,
  UITableCaptionProps
>(({ className, ...props }, ref) => (
  <TableCaption
    ref={ref}
    className={cn(
      "caption-bottom mt-2 text-sm text-muted-foreground",
      className
    )}
    {...props}
  />
))

UITable.displayName = "UITable"
UITableHeader.displayName = "UITableHeader"
UITableBody.displayName = "UITableBody"
UITableFooter.displayName = "UITableFooter"
UITableRow.displayName = "UITableRow"
UITableHead.displayName = "UITableHead"
UITableCell.displayName = "UITableCell"
UITableCaption.displayName = "UITableCaption"